import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import api from '@/services/api'
import { registerPasskey, passkeysSupported } from '@/auth/passkeys'
import { CheckCircle2, KeyRound } from 'lucide-react'
import { errorDetail } from './totpClient'

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-stone-300">{children}</h2>
}

type PasskeyCredential = {
  id: string
  deviceName: string
  createdAt?: string | null
  lastUsedAt?: string | null
}

type PasskeySecurityPanelProps = {
  /** When true, omits outer heading — parent column supplies the title. */
  embedded?: boolean
}

export default function PasskeySecurityPanel({ embedded = false }: PasskeySecurityPanelProps) {
  const [supported] = useState(() => passkeysSupported())
  const [credentials, setCredentials] = useState<PasskeyCredential[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [stepUpCode, setStepUpCode] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ credentials: PasskeyCredential[]; enabled: boolean }>('/auth/passkeys/status')
      setCredentials(res.data.credentials)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setError('Passkey endpoints are not available on this server yet. Deploy the latest API to enable passkeys.')
      } else {
        setError(errorDetail(err, err instanceof Error ? err.message : 'Could not load passkey status'))
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const handleRegister = async () => {
    setBusy(true)
    setError(null)
    setMessage(null)
    const stepUp = { code: stepUpCode.trim() || undefined }
    try {
      const begin = await api.post<{ options: Record<string, unknown> }>(
        '/auth/passkeys/register/begin',
        stepUp,
      )
      const payload = await registerPasskey(begin.data.options, 'Primary device')
      await api.post('/auth/passkeys/register/finish', { ...payload, ...stepUp })
      setMessage('Passkey registered. You can now sign in without a password on this device.')
      setStepUpCode('')
      await refresh()
    } catch (err) {
      setError(errorDetail(err, err instanceof Error ? err.message : 'Passkey registration failed'))
    } finally {
      setBusy(false)
    }
  }

  const handleRemove = async (credentialId: string) => {
    setBusy(true)
    setError(null)
    try {
      await api.delete(`/auth/passkeys/${credentialId}`)
      await refresh()
    } catch (err) {
      setError(errorDetail(err, err instanceof Error ? err.message : 'Could not remove passkey'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      {!embedded ? <SectionHeading>Passkeys (Recommended)</SectionHeading> : null}
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.05] px-3 py-2.5 text-[11px] leading-relaxed text-stone-300">
        Passkeys use device biometrics or a security key. They are phishing-resistant and preferred over
        passwords. Adding one needs a session that already passed two-factor sign-in (authenticator or an
        existing passkey). A password-only session cannot register a passkey; if you just set up an
        authenticator in this session, enter a current code below.
      </div>

      {!supported ? (
        <div className="text-[11px] text-stone-500">Passkeys are not supported in this browser.</div>
      ) : null}

      {loading ? <div className="text-[11px] text-stone-500">Loading passkeys…</div> : null}
      {error ? <div className="text-[11px] text-red-300">{error}</div> : null}
      {message ? (
        <div className="flex items-center gap-2 text-[11px] text-emerald-400">
          <CheckCircle2 size={12} /> {message}
        </div>
      ) : null}

      <div className="space-y-2">
        {credentials.map((cred) => (
          <div
            key={cred.id}
            className="flex items-center justify-between rounded-lg border border-amber-400/10 bg-black/30 px-3 py-2"
          >
            <div>
              <div className="text-[12px] text-stone-100">{cred.deviceName}</div>
              <div className="text-[10px] text-stone-500">
                {cred.lastUsedAt ? `Last used ${new Date(cred.lastUsedAt).toLocaleString()}` : 'Not used yet'}
              </div>
            </div>
            <button
              type="button"
              className="text-[10px] text-red-400 hover:text-red-300 disabled:opacity-50"
              disabled={busy}
              onClick={() => void handleRemove(cred.id)}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <label className="block space-y-1">
        <span className="text-[10px] uppercase tracking-[0.14em] text-stone-500">
          Authenticator code (step-up, if this session is password-only)
        </span>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={stepUpCode}
          onChange={(event) => setStepUpCode(event.target.value)}
          placeholder="123456"
          className="w-full rounded-md border border-amber-400/15 bg-black/40 px-3 py-1.5 text-[12px] text-stone-100"
          disabled={busy}
        />
      </label>

      <button
        type="button"
        className="btn-primary w-full text-[12px] disabled:opacity-50"
        disabled={!supported || busy}
        onClick={() => void handleRegister()}
      >
        {busy ? 'Working…' : credentials.length > 0 ? 'Register another passkey' : 'Register a passkey'}
      </button>
    </div>
  )
}

export function PasswordFallbackNotice() {
  return (
    <div className="rounded-lg border border-amber-400/10 bg-black/20 px-4 py-3 text-[11px] text-stone-400">
      Password login remains available as a fallback, but passkeys are the preferred authentication method.
    </div>
  )
}

export function PasskeyLoginHint() {
  return (
    <div className="flex items-center gap-2 text-[11px] text-stone-400">
      <KeyRound size={12} className="text-amber-300" />
      Prefer signing in with a passkey when available.
    </div>
  )
}
